import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CodeTable from './CodeTable.js';
import CommentsList from '../Comment/List.js';
import PermissionsError from '../Errors/Permission.js';
import ReviewStatus from './ReviewStatus.js';
import UserAvatar from '../UI/Avatar.js';

import { getCode } from '../../Helpers/Code.js';
import { getCodeLinesComments, getComments } from '../../Helpers/Comment.js';

function ViewCode(props) {
  const { t } = useTranslation();
  let { id } = useParams();
  const [addCommentSuccess, setAddCommentSuccess] = useState(0);
  const [code, setCode] = useState([]);
  const [codeLinesComments, setCodeLinesComments] = useState({});
  const [comments, setComments] = useState([]);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('review');

  function updateComments() {
    setAddCommentSuccess(addCommentSuccess + 1);
  }

  useEffect(() => {
    getCode(id).then(data => {
      if (data.success === 1) {
        setCode(data.code);
      } else {
        setError(data.error);
      }
    });
  }, [addCommentSuccess, id]);

  useEffect(() => {
    getComments(id).then(data => {
      if (data.success === 1) {
        setCodeLinesComments(getCodeLinesComments(data.comments));
        setComments(
          data.comments.filter(comment => comment.line_number === null)
        );
      } else {
        setError(data.error);
      }
    });
  }, [addCommentSuccess, id]);

  if (error) return <PermissionsError error={error} />;

  const totalComments =
    comments.length +
    Object.values(codeLinesComments).reduce((a, v) => a + v.length, 0);

  return (
    <div className="cs-review">
      {/* Main panel */}
      <div className="cs-review__main">
        {/* Breadcrumb + header */}
        <div className="cs-rh">
          <div className="cs-rh__breadcrumbs">
            <Link to="/code/feed">{t('Code feed')}</Link>
            <ChevRight />
            <span>{code.author || code.user_id || '—'}</span>
            <ChevRight />
            <span>#{id ? id.slice(0, 8) : '—'}</span>
          </div>
          <div className="cs-rh__row">
            <div className="cs-rh__title">
              <h1>{code.title || id}</h1>
              <div className="cs-rh__meta">
                {code.author && (
                  <div className="cs-rh__meta-item">
                    <span className="label">{t('author')}</span>
                    <UserAvatar
                      user={code.author}
                      email={code.author_email}
                      size="xs"
                    />
                    <span className="val" title={code.author}>
                      {code.author}
                    </span>
                  </div>
                )}
                {code.assignee && (
                  <div className="cs-rh__meta-item">
                    <span className="label">{t('reviewer')}</span>
                    <UserAvatar
                      user={code.assignee}
                      email={code.assignee_email}
                      size="xs"
                    />
                    <span className="val" title={code.assignee}>
                      {code.assignee}
                    </span>
                  </div>
                )}
                {code.language && (
                  <div className="cs-rh__meta-item">
                    <span className="label">{t('lang')}</span>
                    <span className="val val--mono">{code.language}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="cs-rh__actions">
              <ReviewStatus
                code={code}
                userData={props.userData}
              />
            </div>
          </div>

          <div className="cs-rh__tabs">
            <TabBtn active={tab === 'review'} onClick={() => setTab('review')}>
              <EyeIcon /> {t('Review')} <TabBadge>{totalComments}</TabBadge>
            </TabBtn>
            <TabBtn
              active={tab === 'activity'}
              onClick={() => setTab('activity')}
            >
              <ClockIcon /> {t('Comments')}
            </TabBtn>
          </div>
        </div>

        {tab === 'review' && (
          <>
            {code.source_code && (
              <CodeTable
                code={code}
                codeLinesComments={codeLinesComments}
                updateComments={updateComments}
                userData={props.userData}
              />
            )}

            <div className="cs-convo">
              <h2>{t('General comments')}</h2>
              <CommentsList
                addCommentVisibility={1}
                codeId={id}
                comments={comments}
                id="comments-list"
                updateComments={updateComments}
                userData={props.userData}
              />
            </div>
          </>
        )}

        {tab === 'activity' && (
          <div className="cs-convo" style={{ marginTop: 22 }}>
            <h2>{t('All comments')}</h2>
            <CommentsList
              addCommentVisibility={1}
              codeId={id}
              comments={comments}
              id="comments-list"
              updateComments={updateComments}
              userData={props.userData}
            />
          </div>
        )}
      </div>

      {/* Side panel */}
      <aside className="cs-review__side">
        <div className="cs-rs__sect">
          <h3>{t('Details')}</h3>
          <div className="cs-rs__row">
            <span className="k">{t('id')}</span>
            <span className="v">{id ? '#' + id.slice(0, 8) : '—'}</span>
          </div>
          <div className="cs-rs__row">
            <span className="k">{t('language')}</span>
            <span className="v">{code.language || '—'}</span>
          </div>
          <div className="cs-rs__row">
            <span className="k">{t('author')}</span>
            <span className="v">
              {(code.author || code.user_id) && (
                <UserAvatar
                  user={code.author || code.user_id}
                  email={code.author_email}
                  size="xs"
                />
              )}
              {code.author || code.user_id || '—'}
            </span>
          </div>
          <div className="cs-rs__row">
            <span className="k">{t('reviewer')}</span>
            <span className="v">
              {code.assignee
                ? (
                  <>
                    <UserAvatar
                      user={code.assignee}
                      email={code.assignee_email}
                      size="xs"
                    />
                    {code.assignee}
                  </>
                )
                : t('unassigned')}
            </span>
          </div>
          <div className="cs-rs__row">
            <span className="k">{t('status')}</span>
            <span
              className="v"
              style={{ color: statusColor(code.review_status) }}
            >
              {code.review_status || '—'}
            </span>
          </div>
        </div>

        {code.date_posted && (
          <div className="cs-rs__sect">
            <h3>{t('Timeline')}</h3>
            <div className="cs-rs__row">
              <span className="k">{t('opened')}</span>
              <span className="v">
                {code.date_posted ? code.date_posted.slice(0, 16) : '—'}
              </span>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function statusColor(status) {
  const m = {
    SOLVED: 'var(--st-solved)',
    PENDING: 'var(--st-pending)',
    NOT_REQUESTED: 'var(--st-open)',
  };
  return m[status] || 'var(--fg-2)';
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      className={`cs-rh__tab${active ? ' active' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function TabBadge({ children }) {
  return <span className="badge">{children}</span>;
}

function ChevRight() {
  return (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2" />
    </svg>
  );
}

export default ViewCode;
